/*
 * Wine Case Animation
 * --------------------
 * Full-screen, line-art animation that fades in/out when an eligible
 * "Bottle" item is added to the cart.
 *
 * Eligible item  = cart line item whose variant_title contains "bottle"
 *                  (case-insensitive).
 * Behaviour (by eligible quantity in cart AFTER an add):
 *   1..5  -> a bottle FLIES into the 3D wine case; open lid reads
 *            "Complete your case and get 10% off".
 *   6     -> the 6th bottle flies in (case goes 5 -> 6 full), then the lid
 *            closes & is sealed, and a champagne cork pops with fireworks:
 *            "10% off achieved!".
 *   7+    -> no animation.
 *
 * Trigger: the theme's `cart-update` pub/sub event (published after every
 * add), then re-read /cart.js for the authoritative eligible count. No global
 * fetch/XHR patching, to stay compatible with booking apps such as Cowlendar.
 */
(function () {
  'use strict';

  if (window.__wineCaseAnimationLoaded) return;
  window.__wineCaseAnimationLoaded = true;

  var ELIGIBLE_WORD = 'bottle';
  var CASE_SLOTS = 6;
  var COMPLETE_AT = 6;
  var prevBottle = null, prevCase = null;
  var isPlaying = false;
  var refreshTimer = null;
  var timers = [];
  var WINE_COLLECTION = (typeof window !== 'undefined' && window.__wcaWineCollection) || 'all-wines';
  var wineIds = null, wineLoad = null;

  /* --------------------------------------------------------- geometry --- */
  var FTL = [150, 320], d = [78, -52];
  function P(u, v) { return [FTL[0] + 320 * u + d[0] * v, FTL[1] + d[1] * v]; }
  var SLOTS = [];
  [[0.72, 0.58], [0.28, 0.72]].forEach(function (rv) {
    [1 / 6, 1 / 2, 5 / 6].forEach(function (u) {
      var p = P(u, rv[0]);
      SLOTS.push({ x: p[0], y: p[1], s: rv[1] });
    });
  });
  SLOTS.forEach(function (sl, i) { sl.fill = i + 1; });

  var WINE = 'M-22 0 L-22 -92 C-22 -107 -6 -103 -6 -116 L-6 -146 L-9 -151 L9 -151 L6 -146 L6 -116 C6 -103 22 -107 22 -92 L22 0 Z';
  var CHAMP = 'M-20 0 L-20 -86 C-20 -101 -6 -98 -6 -112 L-6 -150 L6 -150 L6 -112 C6 -98 20 -101 20 -86 L20 0 Z';

  /* ------------------------------------------------------------ styles --- */
  var CSS = [
    '.wca-overlay{position:fixed;inset:0;z-index:99999;display:flex;align-items:center;justify-content:center;',
    'background:rgba(244,241,234,0.94);opacity:0;transition:opacity .3s ease;pointer-events:none;',
    'font-family:neue-haas-grotesk-display,Helvetica,Arial,sans-serif;}',
    '.wca-overlay.wca-show{opacity:1;}',
    '.wca-stage{width:min(88vw,520px);}',
    '.wca-svg{width:100%;height:auto;display:block;overflow:visible;}',
    '.wca-svg .ln{fill:#f4f1ea;stroke:#2a2a2a;stroke-width:3;stroke-linecap:round;stroke-linejoin:round;}',
    '.wca-svg .th{fill:none;stroke:#2a2a2a;stroke-width:2;stroke-linecap:round;stroke-linejoin:round;}',
    '.wca-svg .thf{fill:#f4f1ea;stroke:#2a2a2a;stroke-width:2;stroke-linecap:round;stroke-linejoin:round;}',
    '.wca-svg text{fill:#2a2a2a;font-family:inherit;}',
    '.wca-fly{transform-box:fill-box;transform-origin:50% 100%;}',
    '.wca-fly.go{animation:wca-flyin .8s cubic-bezier(.3,.75,.35,1.05) both;}',
    '@keyframes wca-flyin{0%{transform:translate(120px,-380px) rotate(48deg);opacity:0;}',
    '25%{opacity:1;}70%{transform:translate(0,-16px) rotate(-6deg);opacity:1;}',
    '100%{transform:translate(0,0) rotate(0);opacity:1;}}',
    '.wca-sink.sunk{transform:translateY(75px);}',
    '.wca-sink.go{animation:wca-sink-kf .5s .68s cubic-bezier(.45,0,.55,1) both;}',
    '@keyframes wca-sink-kf{0%{transform:translateY(0);}100%{transform:translateY(75px);}}',
    '.wca-seal .wca-sink{animation:wca-drop .5s ease-in both;}',
    '@keyframes wca-drop{from{transform:translateY(75px);}to{transform:translateY(185px);}}',
    '.wca-lid-open{transform-box:fill-box;transform-origin:50% 100%;}',
    '.wca-overlay:not(.wca-fill) .wca-lid-open{animation:wca-lid-out .45s ease both;}',
    '@keyframes wca-lid-out{to{opacity:0;transform:translateY(-12px) scaleY(.9);}}',
    '.wca-lid-closed{opacity:0;transform-box:fill-box;transform-origin:50% 100%;}',
    '.wca-seal .wca-lid-closed{animation:wca-lid-in .5s cubic-bezier(.3,.9,.4,1.4) both;}',
    '@keyframes wca-lid-in{0%{opacity:0;transform:translateY(-22px) scaleY(.55);}70%{opacity:1;}100%{opacity:1;transform:none;}}',
    '.wca-fill .wca-lid-closed{display:none;}',
    '.wca-stamp{opacity:0;transform-box:fill-box;transform-origin:50% 50%;}',
    '.wca-seal .wca-stamp{animation:wca-pop .4s .45s cubic-bezier(.2,.8,.3,1.6) both;}',
    '@keyframes wca-pop{0%{opacity:0;transform:scale(0);}70%{opacity:1;transform:scale(1.18);}100%{opacity:1;transform:scale(1);}}',
    '.wca-champ,.wca-fw,.wca-head{opacity:0;}',
    '.wca-overlay:not(.wca-pop) .wca-champ,.wca-overlay:not(.wca-pop) .wca-fw,.wca-overlay:not(.wca-pop) .wca-head{display:none;}',
    '.wca-pop .wca-champ{animation:wca-champ-in .5s cubic-bezier(.25,.9,.4,1.2) both;}',
    '@keyframes wca-champ-in{0%{opacity:0;transform:translate(60px,70px);}100%{opacity:1;transform:none;}}',
    '.wca-cork{transform-box:fill-box;transform-origin:50% 100%;}',
    '.wca-pop .wca-cork{animation:wca-cork .7s .35s cubic-bezier(.2,.7,.3,1) both;}',
    '@keyframes wca-cork{0%{transform:translate(0,0) rotate(0);opacity:1;}',
    '70%{opacity:1;}100%{transform:translate(26px,-150px) rotate(220deg);opacity:0;}}',
    '.wca-spray{transform-box:fill-box;transform-origin:50% 100%;opacity:0;}',
    '.wca-pop .wca-spray{animation:wca-spray .6s .35s ease-out both;}',
    '@keyframes wca-spray{0%{opacity:0;transform:scale(.2);}40%{opacity:1;}100%{opacity:0;transform:scale(1.3) translateY(-14px);}}',
    '.wca-fw{transform-box:fill-box;transform-origin:50% 50%;}',
    '.wca-pop .wca-fw{animation:wca-burst 1s .5s both;}',
    '.wca-pop .wca-fw.fw2{animation-delay:.85s;}',
    '.wca-pop .wca-fw.fw3{animation-delay:1.15s;}',
    '@keyframes wca-burst{0%{opacity:0;transform:scale(.2);}25%{opacity:1;}100%{opacity:0;transform:scale(1.5);}}',
    '.wca-pop .wca-head{animation:wca-pop .5s .55s cubic-bezier(.2,.8,.3,1.6) both;}',
    '@media (prefers-reduced-motion: reduce){.wca-fly.go,.wca-pop *,.wca-seal *{animation-duration:.001s !important;}}'
  ].join('');

  /* ----------------------------------------------------------- drawing --- */
  function bottle(path, scale) {
    return '<g clip-path="url(#wca-rim)"><g class="wca-fly"><g transform="scale(' + scale + ')"><g class="wca-sink">' +
      '<path class="ln" d="' + path + '"/>' +
      '<rect class="th" x="-15" y="-60" width="30" height="28" rx="2"/>' +
      '<line class="th" x1="-6" y1="-138" x2="6" y2="-138"/>' +
      '</g></g></g></g>';
  }

  function crate() {
    var FBL = [150, 470], FBR = [470, 470], FTR = [470, 320];
    var BTL = [FTL[0] + d[0], FTL[1] + d[1]], BTR = [FTR[0] + d[0], FTR[1] + d[1]], BBR = [FBR[0] + d[0], FBR[1] + d[1]];
    function L(a, b) { return '<line class="th" x1="' + a[0].toFixed(1) + '" y1="' + a[1].toFixed(1) + '" x2="' + b[0].toFixed(1) + '" y2="' + b[1].toFixed(1) + '"/>'; }
    var s = '';
    s += '<path class="ln" d="M' + FBR + ' L' + BBR + ' L' + BTR + ' L' + FTR + ' Z"/>';
    s += '<path class="ln" d="M' + FBL + ' L' + FBR + ' L' + FTR + ' L' + FTL + ' Z"/>';
    s += '<path class="ln" d="M' + FTL + ' L' + FTR + ' L' + BTR + ' L' + BTL + ' Z"/>';
    s += L(P(1 / 3, 0), P(1 / 3, 1)) + L(P(2 / 3, 0), P(2 / 3, 1)) + L(P(0, 0.5), P(1, 0.5));
    [0.33, 0.66].forEach(function (t) {
      s += L([FBR[0] + (BBR[0] - FBR[0]) * t, FBR[1] + (BBR[1] - FBR[1]) * t],
             [FTR[0] + (BTR[0] - FTR[0]) * t, FTR[1] + (BTR[1] - FTR[1]) * t]);
    });
    var logo = (typeof window !== 'undefined' && window.__wcaLogoUrl) ? window.__wcaLogoUrl : '';
    if (logo) {
      s += '<image href="' + logo + '" xlink:href="' + logo + '" x="250" y="335" width="120" height="120" preserveAspectRatio="xMidYMid meet"/>';
    } else {
      s += '<text x="310" y="420" text-anchor="middle" font-family="Georgia, serif" font-size="84" fill="#2a2a2a">HS</text>';
    }
    return '<g>' + s + '</g>';
  }

  function lidOpen() {
    var BTL = [FTL[0] + d[0], FTL[1] + d[1]], BTR = [470 + d[0], 320 + d[1]];
    var tL = [BTL[0] + 12, BTL[1] - 162], tR = [BTR[0] + 12, BTR[1] - 162];
    var cx = (BTL[0] + BTR[0]) / 2 + 12;
    return '<g class="wca-lid-open">' +
      '<path class="ln" d="M' + BTL + ' L' + BTR + ' L' + tR + ' L' + tL + ' Z"/>' +
      '<text x="' + cx + '" y="150" text-anchor="middle" font-size="21" font-weight="700">Complete your case</text>' +
      '<text x="' + cx + '" y="178" text-anchor="middle" font-size="21" font-weight="700">and get 10% off</text>' +
      '</g>';
  }

  function lidClosed() {
    var FTR = [470, 320], BTL = [FTL[0] + d[0], FTL[1] + d[1]], BTR = [FTR[0] + d[0], FTR[1] + d[1]];
    var c = P(0.5, 0.5);
    var s = '<g class="wca-lid-closed">';
    s += '<path class="ln" d="M' + FTL + ' L' + FTR + ' L' + BTR + ' L' + BTL + ' Z"/>';
    [0.4, 0.7].forEach(function (v) {
      var a = P(0, v), b = P(1, v);
      s += '<line class="th" x1="' + a[0].toFixed(1) + '" y1="' + a[1].toFixed(1) + '" x2="' + b[0].toFixed(1) + '" y2="' + b[1].toFixed(1) + '"/>';
    });
    s += '<g class="wca-stamp"><circle class="ln" cx="' + c[0].toFixed(0) + '" cy="' + c[1].toFixed(0) + '" r="22"/>' +
      '<path class="th" d="M' + (c[0] - 9).toFixed(0) + ' ' + c[1].toFixed(0) + ' l9 9 l9 -16"/></g>';
    return s + '</g>';
  }

  function champagne() {
    var spray = '';
    [-24, -6, 12, 28].forEach(function (a) {
      var r = (a - 90) * Math.PI / 180;
      spray += '<line class="th" x1="0" y1="-156" x2="' + (Math.cos(r) * 30).toFixed(0) + '" y2="' + (-156 + Math.sin(r) * 30).toFixed(0) + '"/>';
    });
    return '<g class="wca-champ"><g transform="translate(470,492) rotate(-50)">' +
      '<path class="ln" d="' + CHAMP + '"/>' +
      '<rect class="th" x="-13" y="-58" width="26" height="30" rx="2"/>' +
      '<path class="th" d="M-6 -126 L6 -126 L6 -150 L-6 -150 Z"/>' +
      '<g class="wca-spray">' + spray + '</g>' +
      '<g class="wca-cork"><rect class="ln" x="-9" y="-168" width="18" height="20" rx="6"/>' +
      '<rect class="th" x="-9" y="-150" width="18" height="6"/></g>' +
      '</g></g>';
  }

  function firework(cx, cy, cls) {
    var s = '';
    for (var i = 0; i < 8; i++) {
      var a = Math.PI * 2 * i / 8;
      s += '<line class="th" x1="' + cx + '" y1="' + cy + '" x2="' + (cx + Math.cos(a) * 30).toFixed(0) + '" y2="' + (cy + Math.sin(a) * 30).toFixed(0) + '"/>';
      s += '<circle class="thf" cx="' + (cx + Math.cos(a) * 40).toFixed(0) + '" cy="' + (cy + Math.sin(a) * 40).toFixed(0) + '" r="2.5"/>';
    }
    return '<g class="wca-fw ' + cls + '">' + s + '</g>';
  }

  function buildSVG() {
    var bottles = '';
    SLOTS.forEach(function (sl) {
      bottles += '<g class="wca-bottle" data-fill="' + sl.fill + '" transform="translate(' + sl.x.toFixed(0) + ',' + sl.y.toFixed(0) + ')" style="display:none">' + bottle(WINE, sl.s) + '</g>';
    });
    return '<svg class="wca-svg" viewBox="0 0 640 600" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">' +
      '<defs><clipPath id="wca-rim" clipPathUnits="userSpaceOnUse"><rect x="-400" y="-1200" width="800" height="1200"/></clipPath></defs>' +
      '<g class="wca-head"><text x="320" y="52" text-anchor="middle" font-size="36" font-weight="700" letter-spacing="1">10% off achieved!</text></g>' +
      lidOpen() + crate() + '<g class="wca-bottles">' + bottles + '</g>' + lidClosed() + champagne() +
      firework(118, 150, 'fw1') + firework(522, 138, 'fw2') + firework(330, 92, 'fw3') +
      '</svg>';
  }

  /* ------------------------------------------------------------ overlay -- */
  var overlayEl = null;
  function ensureOverlay() {
    if (overlayEl) return overlayEl;
    var style = document.createElement('style');
    style.textContent = CSS;
    document.head.appendChild(style);
    overlayEl = document.createElement('div');
    overlayEl.className = 'wca-overlay';
    overlayEl.setAttribute('aria-hidden', 'true');
    overlayEl.innerHTML = '<div class="wca-stage">' + buildSVG() + '</div>';
    document.body.appendChild(overlayEl);
    return overlayEl;
  }

  function clearTimers() { timers.forEach(clearTimeout); timers = []; }
  function later(fn, ms) { timers.push(setTimeout(fn, ms)); }

  function showBottles(n, flyIndex) {
    var nodes = overlayEl.querySelectorAll('.wca-bottle');
    for (var i = 0; i < nodes.length; i++) {
      var fi = +nodes[i].getAttribute('data-fill');
      var fly = nodes[i].querySelector('.wca-fly');
      var sink = nodes[i].querySelector('.wca-sink');
      fly.classList.remove('go');
      sink.classList.remove('go'); sink.classList.remove('sunk');
      nodes[i].style.display = fi <= n ? '' : 'none';
      if (fi === flyIndex) { void fly.getBBox(); fly.classList.add('go'); sink.classList.add('go'); }
      else if (fi <= n) { sink.classList.add('sunk'); }
    }
  }

  function fadeOut(delay) {
    later(function () {
      overlayEl.classList.remove('wca-show');
      later(function () { overlayEl.className = 'wca-overlay'; isPlaying = false; }, 340);
    }, delay);
  }

  function playFill(count) {
    ensureOverlay(); clearTimers(); isPlaying = true;
    overlayEl.className = 'wca-overlay wca-fill';
    showBottles(count, count);
    requestAnimationFrame(function () { overlayEl.classList.add('wca-show'); });
    fadeOut(2050);
  }

  function playCelebration(skipFly) {
    ensureOverlay(); clearTimers(); isPlaying = true;
    overlayEl.className = 'wca-overlay wca-fill';
    showBottles(CASE_SLOTS, skipFly ? 0 : CASE_SLOTS);
    requestAnimationFrame(function () { overlayEl.classList.add('wca-show'); });
    var sealAt = skipFly ? 700 : 1300;
    var popAt = skipFly ? 1400 : 2050;
    later(function () { overlayEl.className = 'wca-overlay wca-show wca-seal'; }, sealAt);
    later(function () { overlayEl.className = 'wca-overlay wca-show wca-seal wca-pop'; }, popAt);
    fadeOut(skipFly ? 4100 : 4700);
  }

  /* --------------------------------------------------------- cart logic -- */
  // Load product IDs in the "All Wines" collection (animations are gated to these).
  function loadWineIds() {
    wineIds = {};
    var page = 1;
    function pull() {
      return fetch('/collections/' + WINE_COLLECTION + '/products.json?limit=250&page=' + page, { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (data) {
          if (!data || !data.products || !data.products.length) return;
          data.products.forEach(function (p) { wineIds[p.id] = true; });
          if (data.products.length === 250 && page < 8) { page++; return pull(); }
        }).catch(function () {});
    }
    return pull();
  }
  function ensureWineIds() {
    if (wineIds) return Promise.resolve();
    if (!wineLoad) wineLoad = loadWineIds();
    return wineLoad;
  }
  // 'case'  = full case variant (e.g. "Case (6 Bottles)") -> instant completed celebration
  // 'bottle'= single bottle variant -> counts toward the build-up
  // 'none'  = not eligible (not in All Wines, or no matching variant title)
  function classify(item) {
    if (!wineIds || !wineIds[item.product_id]) return 'none';
    var vt = (item.variant_title || '').toLowerCase();
    if (vt.indexOf('case') !== -1) return 'case';
    if (vt.indexOf(ELIGIBLE_WORD) !== -1) return 'bottle';
    return 'none';
  }
  function counts(cart) {
    var b = 0, c = 0;
    if (cart && cart.items) cart.items.forEach(function (item) {
      var k = classify(item);
      if (k === 'bottle') b += (item.quantity || 0);
      else if (k === 'case') c += (item.quantity || 0);
    });
    return { bottle: b, cases: c };
  }
  function getCart() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); }).catch(function () { return null; });
  }
  function evaluate() {
    return ensureWineIds().then(getCart).then(function (cart) {
      if (!cart) return;
      var n = counts(cart);
      if (prevBottle === null) { prevBottle = n.bottle; prevCase = n.cases; return; }
      var caseUp = n.cases > prevCase;
      var bottleUp = n.bottle > prevBottle;
      prevBottle = n.bottle; prevCase = n.cases;
      if (isPlaying) return;
      if (caseUp) { playCelebration(true); return; }      // case variant -> skip build-up
      if (!bottleUp) return;
      if (n.bottle >= 1 && n.bottle <= COMPLETE_AT - 1) playFill(n.bottle);
      else if (n.bottle === COMPLETE_AT) playCelebration(false);
    });
  }
  function scheduleEvaluate() { clearTimeout(refreshTimer); refreshTimer = setTimeout(evaluate, 180); }

  // Cart-change detection uses the theme's own pub/sub event (published after
  // every add in product-variants.js). We deliberately do NOT patch the global
  // window.fetch / XMLHttpRequest here: those are the same hooks booking apps
  // like Cowlendar use, and overriding them site-wide broke their add-to-cart.
  var pubsubTries = 0;
  function hookPubSub() {
    if (typeof window.subscribe === 'function') {
      try { window.subscribe('cart-update', scheduleEvaluate); } catch (e) {}
    } else if (pubsubTries++ < 20) {
      setTimeout(hookPubSub, 300); // pubsub.js may not have initialised yet
    }
  }

  function init() {
    ensureWineIds().then(getCart).then(function (c) { var n = counts(c); prevBottle = n.bottle; prevCase = n.cases; });
    hookPubSub();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();

  window.wineCaseAnimation = {
    preview: function (n) { playFill(Math.max(1, Math.min(CASE_SLOTS - 1, n || 1))); },
    celebrate: function () { playCelebration(false); },
    celebrateCase: function () { playCelebration(true); },
    _evaluate: evaluate
  };
})();
